import { useEffect, useState } from 'react';
import { prototyperService, type ConversationPrototypeWithVersions } from '@ideoz/core';
import type { SharedPrototypeHistory } from './utils/prototypeVersionMenu';

/**
 * The startup's shared prototypes among a conversation's prototypes (User Story 569). The
 * backend marks them only when the deployment shares one prototype per kind across a startup's
 * scopes (`STARTUP_PROTOTYPES`), so an empty result also means the rule is off.
 */
export function sharedPrototypesOf(
  prototypes: readonly ConversationPrototypeWithVersions[]
): ConversationPrototypeWithVersions[] {
  return prototypes.filter((p) => p.startupPrototype === true);
}

/**
 * The startup's shared prototypes with every version, made in any scope, for the panel's
 * versions menu. Read like the pills' versions: on open and whenever `refreshKey` changes (a
 * build finished, another tab changed a version). A failure leaves the list empty, and the menu
 * shows this conversation's own versions as before.
 */
export function useSharedPrototypes(args: {
  projectId: string | null | undefined;
  conversationId: string | null | undefined;
  enabled: boolean;
  refreshKey: string;
}): SharedPrototypeHistory[] {
  const { projectId, conversationId, enabled, refreshKey } = args;
  const [shared, setShared] = useState<SharedPrototypeHistory[]>([]);

  useEffect(() => {
    if (!projectId || !conversationId || !enabled) {
      setShared([]);
      return undefined;
    }
    let cancelled = false;
    void (async () => {
      try {
        const prototypes = sharedPrototypesOf(
          await prototyperService.listConversationPrototypes(projectId, conversationId)
        );
        const histories = await Promise.all(
          prototypes.map(async (p) => ({
            id: p.id,
            title: p.title,
            versions: await prototyperService
              .listPrototypeVersions(projectId, conversationId, p.id)
              .catch(() => []),
          }))
        );
        if (!cancelled) setShared(histories);
      } catch {
        if (!cancelled) setShared([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [projectId, conversationId, enabled, refreshKey]);

  return shared;
}
